import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X } from "lucide-react";
import api from "@/lib/api";
import { Coin, fmtK } from "@/lib/points";
import { useFormCfg, PaidKeyCtx } from "@/lib/formcfg";

const CONTACT = ["phone", "whatsapp", "viber", "instagram", "facebook", "tiktok", "website", "email"];
const usedIn = (l, k) => {
  const d = l?.data || {};
  return { photos: (l?.images || []).length > 1, map: l?.lat != null && !d.loc_auto, hours: !!(d.hours_week || []).length, menu: !!(d.menu || []).length && !d.no_menu,
    contact: CONTACT.some((c) => l?.[c]) || !!(d.facebook || d.youtube), proximity: !!l?.proximity_alert, social: !!l?.social_allowed,
    services: !!((d.services || []).length || (d.service_list || []).length), payment: !!(d.payment || []).length, messages: !!l?.allow_messages }[k];
};
const OFF = {
  photos: (p) => { p.images = (p.images || []).slice(0, 1); }, map: (p) => { p.data = { ...p.data, loc_auto: true }; },
  hours: (p) => { p.data = { ...p.data, hours_week: [] }; }, menu: (p) => { p.data = { ...p.data, menu: [] }; },
  contact: (p) => { CONTACT.forEach((c) => { if (c in p) p[c] = ""; }); p.data = { ...p.data, facebook: "", youtube: "" }; },
  proximity: (p) => { p.proximity_alert = false; }, social: (p) => { p.social_allowed = false; }, messages: (p) => { p.allow_messages = false; },
  services: (p) => { p.data = { ...p.data, services: [], service_list: [] }; }, payment: (p) => { p.data = { ...p.data, payment: [] }; },
};

export function useCredits(category, initial) {
  const fcx = useFormCfg();
  const [info, setInfo] = useState(null);
  const [on, setOn] = useState(() => Object.fromEntries(Object.keys(OFF).map((k) => [k, !!usedIn(initial, k)])));
  const [renew, setRenew] = useState(initial?.data?.auto_renew ?? true);
  useEffect(() => { api.get("/credits/info").then((r) => setInfo(r.data)).catch(() => setInfo(false)); }, []);
  return useMemo(() => {
    const cfg = info?.prices?.[category] || {};
    const now = new Date().toISOString();
    const pf = initial?.paid_features || {};
    const label = Object.fromEntries((info?.features || []).map((f) => [f.key, f.label]));
    const prepaid = (k) => pf[k] && (!pf[k].expires_at || pf[k].expires_at > now);
    const price = (k) => (cfg[k]?.off || prepaid(k) ? 0 : Number(cfg[k]?.price || 0));
    const off = (k) => !!cfg[k]?.off;
    const paid = (k) => price(k) > 0;
    const shown = (k) => !off(k) && (!paid(k) || !!on[k]);
    const items = (extra = {}) => [...Object.keys(OFF).filter((k) => paid(k) && on[k]), ...Object.keys(extra).filter((k) => extra[k] && price(k) > 0)]
      .map((k) => ({ key: k, label: label[k] || k, price: price(k) }));
    return {
      ready: !!info, balance: Number(info?.balance || 0), price, off, paid, shown, label, items,
      dur: info?.durations?.[category] || {}, renew, setRenew,
      set: (k, v) => setOn((x) => ({ ...x, [k]: v })),
      maxImg: shown("photos") ? 10 : 1,
      strip: (p) => { Object.keys(OFF).forEach((k) => { if (!shown(k) || fcx.hiddenPaid(k)) OFF[k](p); }); p.data = { ...p.data, credit_on: Object.keys(OFF).filter((k) => paid(k) && on[k]), auto_renew: renew }; return p; },
    };
  }, [info, on, renew, category, initial]);
}

const Badge = ({ cr, k }) => (
  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${cr.paid(k) ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`} data-testid={`credit-badge-${k}`}>{cr.paid(k) ? `${fmtK(cr.price(k))} kredite` : "Falas"}</span>
);

export function Paid(props) {
  const fc = useFormCfg();
  if (!fc.edit && fc.hiddenPaid(props.k)) return null;
  return <PaidKeyCtx.Provider value={props.k}><PaidInner {...props} /></PaidKeyCtx.Provider>;
}

function PaidInner({ cr, k, children }) {
  if (!cr.ready) return children;
  if (cr.off(k)) return null;
  if (!cr.shown(k)) {
    return (
      <button type="button" data-testid={`credit-add-${k}`} onClick={() => cr.set(k, true)} className="w-full mt-4 flex items-center gap-3 rounded-2xl border-2 border-dashed border-amber-300 bg-amber-50 p-4 text-left active:scale-[0.98] transition-transform">
        <span className="w-9 h-9 rounded-full bg-amber-400 text-white flex items-center justify-center shrink-0"><Plus className="w-5 h-5" /></span>
        <span className="flex-1 font-semibold text-slate-800">Shto: {cr.label[k]}</span><Badge cr={cr} k={k} />
      </button>
    );
  }
  return (
    <div className="relative">
      <div className="absolute right-3 top-5 z-10 flex items-center gap-1">
        <Badge cr={cr} k={k} />
        {cr.paid(k) && <button type="button" data-testid={`credit-remove-${k}`} onClick={() => cr.set(k, false)} aria-label="Hiq" className="w-6 h-6 rounded-full bg-white border border-slate-200 text-slate-500 flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>}
      </div>
      {children}
    </div>
  );
}

export function PhotosNote({ cr, count }) {
  if (!cr.ready || cr.off("photos") || !cr.paid("photos") || cr.shown("photos")) return null;
  return (
    <button type="button" data-testid="credit-add-photos" onClick={() => cr.set("photos", true)} className="w-full mt-2 flex items-center justify-between gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
      <span>{count ? "1 foto falas · " : ""}Shto deri në 10 foto</span><Badge cr={cr} k="photos" />
    </button>
  );
}

function RenewBox({ cr }) {
  const days = Number(cr.dur.days || 0);
  if (!days) return null;
  const price = Number(cr.dur.price || 0);
  return (
    <div className="mt-4 bg-white rounded-2xl border border-slate-200 p-4" data-testid="listing-renew-box">
      <div className="text-sm text-slate-700">Shpallja vlen <b>{days} ditë</b> nga aprovimi. Vazhdimi: <b className={price ? "text-amber-600" : "text-emerald-600"}>{price ? `${fmtK(price)} kredite` : "Falas"}</b></div>
      <label className="flex items-center gap-2 mt-2 text-sm font-semibold text-slate-800">
        <input data-testid="listing-auto-renew" type="checkbox" checked={!!cr.renew} onChange={(e) => cr.setRenew(e.target.checked)} className="w-5 h-5 accent-emerald-600" />
        Vazhdo automatikisht kur të skadojë{price ? " (nëse kam GjejKredit)" : ""}
      </label>
      <p className="text-[11px] text-slate-500 mt-1">Nëse nuk ke GjejKredit kur skadon, shpallja mbetet falas: hiqen vetëm pjesët me pagesë.</p>
    </div>
  );
}

export function CreditTotal({ cr, extra }) {
  if (!cr.ready) return null;
  return <><RenewBox cr={cr} /><CostBox cr={cr} extra={extra} /></>;
}

function CostBox({ cr, extra }) {
  const nav = useNavigate();
  if (!cr.ready) return null;
  const items = cr.items(extra);
  const total = Math.round(items.reduce((s, i) => s + i.price, 0) * 100) / 100;
  if (!total) return null;
  const short = total > cr.balance;
  return (
    <div className="mt-4 bg-white rounded-2xl border border-amber-200 p-4" data-testid="credit-total-box">
      <div className="flex items-center gap-2 font-bold text-slate-900"><Coin size={28} />Kostoja me GjejKredit</div>
      <div className="mt-2 space-y-1">
        {items.map((i) => <div key={i.key} className="flex justify-between text-sm"><span className="text-slate-600">{i.label}</span><b className="text-amber-600">{fmtK(i.price)} kredite</b></div>)}
        <div className="flex justify-between text-sm border-t border-slate-100 pt-1"><b>Totali</b><b data-testid="credit-form-total">{fmtK(total)} kredite</b></div>
        <div className="flex justify-between text-xs text-slate-500"><span>Ke</span><span>{fmtK(cr.balance)} kredite</span></div>
      </div>
      {short && (
        <>
          <div className="mt-2 rounded-xl bg-rose-50 border border-rose-200 p-2.5 text-sm font-semibold text-rose-700" data-testid="credit-form-short">🔴 Nuk keni GjejKredit të mjaftueshme. Ju duhen edhe {fmtK(total - cr.balance)} kredite.</div>
          <button type="button" data-testid="credit-form-buy" onClick={() => nav("/gjejkredit/bli")} className="w-full mt-2 h-11 rounded-xl bg-amber-500 text-white font-bold">Bli GjejKredit</button>
        </>
      )}
    </div>
  );
}

export const creditShort = (cr, extra) => cr.ready && cr.items(extra).reduce((s, i) => s + i.price, 0) > cr.balance;
